import { memo, useMemo, useState } from 'react';

import { faDownload, faXRay } from '@fortawesome/pro-solid-svg-icons';
import { State } from '@progress/kendo-data-query';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { GridColumn, GridDataStateChangeEvent, GridHeaderCellProps } from '@progress/kendo-react-grid';
import { MenuSelectEvent } from '@progress/kendo-react-layout';
import { Offset } from '@progress/kendo-react-popup';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { UploadModel } from 'models';

import { useBoolean, useEvent, useOutsideClick } from 'core/hooks';
import { ActionListCell, DataTable, HeaderCell, Page, PageHeader, Popup, Toolbar } from 'core/ui';
import { Action, Button, ButtonVariants, DEFAULT_PAGE_SIZES } from 'core/ui';
import { hasText } from 'core/utils';
import { downloadBlobAsFile2 } from 'core/utils/fileUtils';

import { useMutationDownloadUploadTrackerFile, useMutationExportUploadTrackerData, useQueryUploadTrackerGrid } from 'features/api/hooks';
import { FilterModal } from 'features/exam/fragments/FilterModal';
import { ContextMenuItems } from 'features/upload-tracker/constants';
import { UploadTrackerService } from 'features/upload-tracker/services';

import { GatewayLogModal } from './GatewayLogModal';

type UploadTrackerHomeProps = {
  examId?: number;
  hideHeader?: boolean;
};

const PAGEABLE_SETTINGS = { pageSizes: DEFAULT_PAGE_SIZES };
const DEFAULT_EMPTY_FILTER: CompositeFilterDescriptor = { filters: [], logic: 'and' };
const DEFAULT_CONTEXT_MENU_OFFSET: Offset = { left: 0, top: 0 };

export const UploadTrackerHome = memo<UploadTrackerHomeProps>(({ examId, hideHeader = false }) => {
  const [isFilterModalOpen, { toggle: toggleIsFilterModalOpen, setFalse: closeFilterModal }] = useBoolean(false);
  const [isContextMenuOpen, { setTrue: openContextMenu, setFalse: closeContextMenu }] = useBoolean(false);
  const [contextMenuOffset, setContextMenuOffset] = useState<Offset>(DEFAULT_CONTEXT_MENU_OFFSET);
  const [isGatewayLogsOpen, { setTrue: openGatewayLogs, setFalse: closeGatewayLogs }] = useBoolean(false);
  const [isQueryDebounced, setIsQueryDebounced] = useState(false); // Used to control whether the query should be debounced (true) or immediate (false).
  const [contextMenuUpload, setContextMenuUpload] = useState<UploadModel | null>(null);
  const [searchParams] = useSearchParams();
  const [dataState, setDataState] = useState<State>(() => {
    const initialFilters: CompositeFilterDescriptor['filters'] = [];

    if (searchParams.has('requestUid')) {
      initialFilters.push({ field: 'requestUID', operator: 'contains', value: searchParams.get('requestUid') });
    }

    if (examId != null) {
      initialFilters.push({ field: 'examId', operator: 'eq', value: examId });
    }

    return {
      skip: 0,
      take: 100,
      sort: [{ field: 'id', dir: 'desc' }],
      filter: initialFilters.length > 0 ? { logic: 'and', filters: initialFilters } : DEFAULT_EMPTY_FILTER,
    };
  });

  const uploadsQuery = useQueryUploadTrackerGrid(dataState, isQueryDebounced);
  const downloadFileMutation = useMutationDownloadUploadTrackerFile();
  const exportDataMutation = useMutationExportUploadTrackerData();

  const handleDataStateChange = useEvent((event: GridDataStateChangeEvent) => {
    let isEventFromPager = false;
    let isEventFromSortClick = false;

    // Determine if the event originates within the pager.
    if (event.syntheticEvent.target instanceof Element) {
      isEventFromPager = event.syntheticEvent.target.closest('.k-pager') != null;
    }

    // Determine if the event is the result of the user clicking a sortable header cell.
    if (event.syntheticEvent.type === 'click' && event.syntheticEvent.target instanceof Element) {
      // We have to make sure we are within the separate <table> element for the headers, but we also have to make sure we aren't in the filter row.
      isEventFromSortClick =
        event.syntheticEvent.target.closest('.k-grid-header-table') != null && event.syntheticEvent.target.closest('.k-filter-row') == null;
    }

    setDataState(event.dataState);
    setIsQueryDebounced(!isEventFromPager && !isEventFromSortClick);
  });

  const handleExportCSVClick = useEvent(async () => {
    if (dataState == null) {
      throw new Error('Cannot export CSV because the dataState is null.');
    }

    const result = await exportDataMutation.mutateAsync(dataState);
    downloadBlobAsFile2(result.blob, `${result.filename}.${result.type}`);
  });

  const handleDownloadClick = useEvent(async (file: UploadModel) => {
    if (!downloadFileMutation?.mutateAsync) {
      console.error('downloadFileMutation is not available');
      return;
    }
    if (hasText(file.studyInstanceUID)) {
      const result = await downloadFileMutation.mutateAsync({ studyInstanceUID: file.studyInstanceUID });
      downloadBlobAsFile2(result.blob, `${result.filename}.${result.type}`);
    } else if (file.source === 'Web-Alternative') {
      const result = await downloadFileMutation.mutateAsync({ requestUID: file.requestUID, source: file.source });
      downloadBlobAsFile2(result.blob, `${result.filename}.${result.type}`);
    }
  });

  const handleExamClick = useEvent((file: UploadModel) => {
    window.open(`/exam/${file.examId}/edit`, '_blank');
  });

  const gridActions: Action[] = useMemo(() => {
    return [
      {
        key: 'download',
        title: 'Download',
        icon: faDownload,
        onClick: (_, dataItem) => handleDownloadClick(dataItem),
      },
      {
        key: 'exam',
        title: 'Exam',
        icon: faXRay,
        disabled: (dataItem: UploadModel) => !dataItem.examId,
        onClick: (_, dataItem) => handleExamClick(dataItem),
      },
    ];
  }, [handleDownloadClick, handleExamClick]);

  const handleFilterModalSave = useEvent((filter: CompositeFilterDescriptor) => {
    closeFilterModal();
    setDataState({ ...dataState, filter });
    // React Query will automatically refetch when dataState changes
  });

  const contextMenuRef = useOutsideClick<HTMLDivElement>(() => {
    if (isContextMenuOpen) {
      setContextMenuUpload(null);
      setContextMenuOffset(DEFAULT_CONTEXT_MENU_OFFSET);
      closeContextMenu();
    }
  });

  const handleContextMenuOpen = useEvent((event: MouseEvent, dataItem: UploadModel) => {
    setContextMenuUpload(dataItem);
    setContextMenuOffset({ left: event.clientX, top: event.clientY });
    openContextMenu();
  });

  const handleOnContextMenuSelect = useEvent((event: MenuSelectEvent) => {
    if (event.item.text === ContextMenuItems.GatewayLogs.text) {
      openGatewayLogs();
    }

    closeContextMenu();
  });

  const grid = (
    <>
      <DataTable
        {...dataState}
        data={uploadsQuery?.data}
        filterable
        sortable
        reorderable
        pageable={PAGEABLE_SETTINGS}
        onDataStateChange={handleDataStateChange}
        actions={gridActions}
        resizable
        isLoading={uploadsQuery.isLoading}
        onContextMenuOpen={handleContextMenuOpen}
      >
        {!hideHeader && (
          <Toolbar
            customElements={
              <StyledToolbarLeftDiv>
                <StyledActionButton type="button" onClick={toggleIsFilterModalOpen} variant={ButtonVariants.SECONDARY}>
                  Filter
                </StyledActionButton>

                <StyledActionButton type="button" onClick={handleExportCSVClick} variant={ButtonVariants.SECONDARY}>
                  Export as CSV
                </StyledActionButton>
              </StyledToolbarLeftDiv>
            }
          />
        )}
        <GridColumn title="Action" filterable={false} sortable={false} cell={ActionListCell} width="100px" />

        {UploadTrackerService.Columns.map((column, orderIndex) => {
          return (
            <GridColumn
              key={column.field}
              {...column}
              headerCell={(headerCellProps: GridHeaderCellProps) => <HeaderCell {...headerCellProps} description={column.headerCellDescription} />}
              orderIndex={orderIndex}
              cell={column.cell}
              filter={column.columnFilter}
              columnMenu={column.columnMenu}
            />
          );
        })}
      </DataTable>
      <GatewayLogModal entityType="exam" show={isGatewayLogsOpen} suid={contextMenuUpload?.studyInstanceUID ?? null} onClose={closeGatewayLogs} />
      <Popup ref={contextMenuRef} onSelect={handleOnContextMenuSelect} isOpen={isContextMenuOpen} menuItems={ContextMenuItems} offset={contextMenuOffset} />
    </>
  );

  // Render directly in a div if header is hidden, otherwise use Page component
  if (hideHeader) {
    return (
      <StyledEmbeddedWrapper>
        {' '}
        {/* Add basic padding */}
        {uploadsQuery?.data && grid}
      </StyledEmbeddedWrapper>
    );
  }

  return (
    <Page>
      <PageHeader title="Upload Tracker" />
      {isFilterModalOpen && (
        <FilterModal
          columns={UploadTrackerService.Columns}
          initialFilter={dataState.filter ?? DEFAULT_EMPTY_FILTER}
          onClose={closeFilterModal}
          onSave={handleFilterModalSave}
        />
      )}

      {uploadsQuery?.data && grid}
    </Page>
  );
});

UploadTrackerHome.displayName = 'UploadTrackerHome';

const StyledEmbeddedWrapper = styled.div`
  padding: 15px; /* Add some padding when embedded */
  height: 100%;
  overflow-y: auto;
`;

const StyledToolbarLeftDiv = styled.div`
  display: flex;
  flex: 1 1 0;
`;
const StyledActionButton = styled(Button)`
  margin-right: ${({ theme }) => theme.space.spacing20};
`;
